import { useEffect, useState } from 'react';

import {
  Badge,
  IconButton,
  OffsetPagination,
  Table,
  TableCard,
  TableSkeleton,
  Text,
} from '@opositandopn/ui';
import {
  IoArrowForwardCircleOutline,
  IoClipboardOutline,
} from 'react-icons/io5';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatSortDate } from '../../../../utils/date';
import { useGetMultipleChoiceAssessments } from '../../api/useMultipleChoiceAssessments';
import { AssessmentListFilterForm } from '../assessment-list-filter/AssessmentListFilter';
import { MultipleChoiceAssessmentPreview } from '../multiple-choice-assessment-preview/MultipleChoiceAssessmentPreview';

import styles from './MultipleChoiceAssessmentAdminList.module.scss';

type ContainerProps = BaseComponentProps & {
  filter: AssessmentListFilterForm;
  onSelected?: (assessmentId: string) => void;
};

function MultipleChoiceAssessmentAdminList({
  className,
  filter,
  onSelected,
  testId = 'ui-multiple-choice-assessment-admin-list',
}: ContainerProps) {
  const [current, setCurrent] = useState<string>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetMultipleChoiceAssessments({
    subjectId: filter.subject.id,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: filter.fromTime,
    toTime: filter.toTime,
    sort: filter.sort,
    params: {
      text: filter.text,
      statuses: filter.statuses,
      visibilities: filter.visibilities,
      purposes: filter.purposes,
      referenceStandards: filter.referenceStandards,
      methodologies: filter.methodologies,
      perspectives: filter.perspectives,
      settings: filter.settings,
      difficultyLevels: filter.difficultyLevels,
      tagIds: filter.tags?.map((tag) => tag.id),
      questionIds: filter.questionIds,
      numberOfChoices: filter.numberOfChoices,
      official: filter.official,
      released: filter.released,
    },
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filter]);

  return (
    <>
      <TableCard.Root className={className} data-testid={testId}>
        <TableCard.Header
          title="Simulacros"
          contentTrailing={
            <Badge>
              {response?.totalItems
                ? `Total: ${response?.totalItems}`
                : 'Sin simulacros'}
            </Badge>
          }
        />
        <Table aria-label="Simulacros">
          <Table.Header>
            <Table.Head id="name" label="Nombre" isRowHeader />
            <Table.Head id="questions" label="Preguntas" />
            <Table.Head id="releaseDate" label="Fecha de publicación" />
            <Table.Head id="status" label="Estado" />
            <Table.Head id="actions" />
          </Table.Header>
          {isLoading || response === undefined ? (
            <TableSkeleton columns={5} items={pagination.pageSize} />
          ) : (
            <Table.Body
              items={response.items}
              renderEmptyState={() => 'No hay simulacros'}
            >
              {(item) => (
                <Table.Row id={item.id}>
                  <Table.Cell
                    style={{
                      width: '100%',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.name}
                  </Table.Cell>
                  <Table.Cell>
                    <Text variety="annotation" font="monospace">
                      {item.questions.length} preguntas
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {'releaseDate' in item && item.releaseDate ? (
                      <Text
                        font="monospace"
                        variety="annotation"
                        variant="neutral"
                      >
                        {formatSortDate(new Date(item.releaseDate as Date))}
                      </Text>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    {item.status === 'draft' ? (
                      <Badge bg="warning">Borrador</Badge>
                    ) : item.status === 'removed' ? (
                      <Badge bg="danger">Eliminado</Badge>
                    ) : item.status === 'published' ? (
                      <Badge bg="success">Publicado</Badge>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles['actions']}>
                      <IconButton
                        size="sm"
                        icon={<IoClipboardOutline />}
                        onClick={() => {
                          setCurrent(item.id);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          )}
        </Table>
        {response && (
          <OffsetPagination
            className={styles.pagination}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalRecords={response.totalItems}
            onPageChange={(pageNumber: number) => {
              setPagination({ ...pagination, pageNumber });
            }}
            pageNeighbours={2}
          />
        )}
      </TableCard.Root>
      <ResourceDialog open={current !== undefined}>
        {current && (
          <MultipleChoiceAssessmentPreview
            subjectId={filter.subject.id}
            assessmentId={current}
            onClose={() => {
              setCurrent(undefined);
            }}
          >
            {onSelected && (
              <IconButton
                icon={<IoArrowForwardCircleOutline />}
                onClick={() => {
                  onSelected(current);
                }}
              />
            )}
          </MultipleChoiceAssessmentPreview>
        )}
      </ResourceDialog>
    </>
  );
}

export { MultipleChoiceAssessmentAdminList };
